/**
 * schoolRegistry — beheer van het scholenregister (migratie 0041).
 *
 * WAAROM DIT LOS STAAT VAN `schools.ts`
 *
 * `schools.ts` is de LEESKANT voor een registrerende leerkracht, en die draait
 * vóór inloggen met de anon key. Deze module is de BEHEERKANT en vereist een
 * ingelogde beheerder. Ze in één bestand zetten zou betekenen dat het
 * registratiescherm de admin-RPC's meebundelt — en dan staat de beheercode in
 * de bundel die een niet-ingelogde bezoeker binnenhaalt.
 *
 * DE AUTORISATIE ZIT IN DE DATABASE, NIET HIER.
 * Elke RPC controleert zelf `is_admin()`. Deze module voegt geen eigen controle
 * toe: dat zou een tweede plek zijn waar de regel staat, en bij de volgende
 * wijziging wint de zwakste van de twee.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';

export type SchoolStatus = 'pending' | 'verified' | 'rejected';

export interface School {
  id: string;
  name: string;
  city: string | null;
  brin: string | null;
  status: SchoolStatus;
  dpaSignedAt: string | null;
  createdAt: string | null;
}

/** Eén aangevraagde schoolnaam, met alle leerkrachten die hem intikten. */
export interface SchoolRequest {
  requestedName: string;
  teacherCount: number;
  firstSeenAt: string | null;
  teacherIds: string[];
}

export interface RegistryResult {
  ok: boolean;
  message?: string;
}

/**
 * Alle scholen, ongeacht status.
 *
 * Loopt via een gewone select en niet via een RPC: de policy "admins see all
 * schools" uit 0041 doet het werk al. Een RPC eromheen zou de policy dupliceren
 * zonder er iets aan toe te voegen.
 */
export async function listAllSchools(): Promise<{
  ok: boolean;
  schools: School[];
  message?: string;
}> {
  const {
    data,
    error
  } = await supabase.from('schools').select('id, name, city, brin, status, dpa_signed_at, created_at').order('name');
  if (error) {
    logError('[SchoolRegistry] Register ophalen mislukt', error);
    return {
      ok: false,
      schools: [],
      // Geen "geen scholen gevonden": dat zou een storing als een leeg register
      // presenteren, en dan maakt een beheerder een school aan die er al staat.
      message: 'Het register kon niet worden opgehaald. Dit is geen leeg register — probeer het opnieuw.'
    };
  }
  return {
    ok: true,
    schools: (data ?? []).map(row => ({
      id: row.id as string,
      name: row.name as string,
      city: (row.city as string | null) ?? null,
      brin: (row.brin as string | null) ?? null,
      status: row.status as SchoolStatus,
      dpaSignedAt: (row.dpa_signed_at as string | null) ?? null,
      createdAt: (row.created_at as string | null) ?? null
    }))
  };
}

/**
 * De openstaande aanvragen, gebundeld per genormaliseerde naam.
 *
 * Het bundelen doet de database (`admin_list_school_requests`), niet deze
 * functie. Drie aanvragen voor dezelfde school zijn één beslissing, en die
 * groepering hoort bij de bron te staan zodat elke lezer hem krijgt.
 */
export async function listSchoolRequests(): Promise<{
  ok: boolean;
  requests: SchoolRequest[];
  message?: string;
}> {
  const {
    data,
    error
  } = await supabase.rpc('admin_list_school_requests');
  if (error) {
    logError('[SchoolRegistry] Aanvragen ophalen mislukt', error);
    return {
      ok: false,
      requests: [],
      message: 'De aanvragen konden niet worden opgehaald.'
    };
  }
  const rows = (data ?? []) as Array<{
    requested_name: string;
    teacher_count: number;
    first_seen_at: string | null;
    teacher_ids: string[] | null;
  }>;
  return {
    ok: true,
    requests: rows.map(r => ({
      requestedName: r.requested_name,
      teacherCount: r.teacher_count,
      firstSeenAt: r.first_seen_at,
      teacherIds: r.teacher_ids ?? []
    }))
  };
}

export interface UpsertSchoolInput {
  /** Leeg = nieuw aanmaken. Gevuld = deze rij bijwerken. */
  id?: string;
  name: string;
  city?: string;
  brin?: string;
  status?: SchoolStatus;
  dpaSignedAt?: string | null;
}

/**
 * Maakt een school aan of werkt hem bij.
 *
 * Standaardstatus is `pending`, óók bij een school die wij zelf invoeren. Dat is
 * geen omslachtigheid: "in het register" en "goedgekeurd" moeten twee dingen
 * blijven, anders is er niets meer te verifiëren en betekent opnemen automatisch
 * toelaten.
 */
export async function upsertSchool(input: UpsertSchoolInput): Promise<RegistryResult & {
  id?: string;
}> {
  const {
    data,
    error
  } = await supabase.rpc('admin_upsert_school', {
    p_name: input.name,
    p_city: input.city ?? null,
    p_brin: input.brin ?? null,
    p_school_id: input.id ?? null,
    p_status: input.status ?? null,
    p_dpa_signed_at: input.dpaSignedAt ?? null
  });
  if (error) {
    logError('[SchoolRegistry] Opslaan mislukt', error);
    return {
      ok: false,
      message: describeError(error.message, input.id ? 'De school is niet bijgewerkt. Er is niets gewijzigd.' : 'De school is niet aangemaakt.')
    };
  }
  return {
    ok: true,
    id: String(data)
  };
}

/**
 * Zet de status van een school.
 *
 * Apart van `upsertSchool` omdat dit de handeling met gevolgen is: `verified`
 * maakt de school zichtbaar voor élke registrerende leerkracht in het land. Die
 * beslissing hoort niet als bijproduct van een naamcorrectie te kunnen vallen.
 */
export async function setSchoolStatus(schoolId: string, status: SchoolStatus): Promise<RegistryResult> {
  const {
    error
  } = await supabase.rpc('admin_set_school_status', {
    p_school_id: schoolId,
    p_status: status
  });
  if (error) {
    logError('[SchoolRegistry] Status wijzigen mislukt', error);
    return {
      ok: false,
      message: describeError(error.message, 'De status is niet gewijzigd.')
    };
  }
  return {
    ok: true
  };
}

/**
 * Wijst een aanvraag toe aan een bestaande schoolrij.
 *
 * Geeft het AANTAL bijgewerkte leerkrachten terug, en dat getal is de
 * terugmelding aan de beheerder. Nul is geen fout — de aanvraag kan al door een
 * collega zijn afgehandeld — maar mag niet stil blijven: anders denkt hij iets
 * te hebben opgelost wat er niet meer was. Dat is exact de faalvorm die dit
 * project bij migraties 0030 en 0032 heeft gekost.
 */
export async function resolveSchoolRequest(requestedName: string, schoolId: string): Promise<RegistryResult & {
  updated?: number;
}> {
  const {
    data,
    error
  } = await supabase.rpc('admin_resolve_school_request', {
    p_requested_name: requestedName,
    p_school_id: schoolId
  });
  if (error) {
    logError('[SchoolRegistry] Aanvraag toewijzen mislukt', error);
    return {
      ok: false,
      message: describeError(error.message, 'De aanvraag is niet toegewezen. Er is niets gewijzigd.')
    };
  }
  return {
    ok: true,
    updated: Number(data ?? 0)
  };
}

/**
 * Vertaalt een databasefout naar iets bruikbaars.
 *
 * De RPC's uit 0041 gooien met een Nederlandse tekst wanneer de aanroeper iets
 * verkeerd doet (geen naam, onbekende status, school bestaat niet). Die teksten
 * zijn voor een beheerder bruikbaar en gaan door. Al het andere is een storing,
 * en dan noemt de ruwe Postgres-melding interne functienamen en zegt niets over
 * wat er nu te doen valt.
 */
function describeError(raw: string | undefined, fallback: string): string {
  if (!raw) return fallback;
  const passthrough = ['Alleen een beheerder', 'moet een naam hebben', 'Onbekende status', 'bestaat niet', 'Geen aanvraagnaam'];
  if (passthrough.some(p => raw.includes(p))) return raw;
  return fallback;
}
